import api from './api';

export type WelcomeEmailConfigStatus = {
  enabled: boolean;
  smtpConfigured: boolean;
  supportEmail: string;
  fromEmail: string;
  ready: boolean;
};

export type WelcomeNotifyResult = {
  email?: { delivered: boolean; mode: string; reason?: string };
  sms?: { sent: boolean; skipped?: boolean };
  skipped?: boolean;
  reason?: string;
};

export async function getWelcomeEmailConfig(): Promise<WelcomeEmailConfigStatus> {
  const res = await api.get('/notifications/admin/welcome-email-config');
  return res.data.data as WelcomeEmailConfigStatus;
}

export function formatWelcomeSuccessNote(
  email: string,
  welcome?: WelcomeNotifyResult,
  cfg?: WelcomeEmailConfigStatus,
): string {
  if (welcome?.skipped) {
    return ` ${welcome.reason || 'Welcome notifications are disabled.'}`;
  }
  if (welcome?.email?.delivered) {
    return email ? ` Welcome email sent to ${email}.` : ' Welcome email sent.';
  }
  if (welcome?.email?.mode === 'log') {
    return ' Welcome email logged only — configure SMTP in backend .env and restart the server.';
  }
  if (welcome?.email?.reason) {
    return ` Welcome email not delivered: ${welcome.email.reason}`;
  }
  if (welcome?.email?.mode === 'smtp' && !welcome?.email?.delivered) {
    return ' Welcome email failed to send — check backend logs and SMTP credentials.';
  }
  if (cfg && !cfg.smtpConfigured) {
    return ' Welcome email not delivered — set SMTP_HOST, SMTP_USER, SMTP_PASS, EMAIL_FROM in backend .env.';
  }
  // Backend did not return delivery status (often old server process — restart backend after .env changes)
  if (cfg?.smtpConfigured && email) {
    return ` Welcome email processed for ${email} — check inbox and spam.`;
  }
  if (email) {
    return ` Welcome email requested for ${email} — restart backend after SMTP setup to confirm delivery status.`;
  }
  return ' Welcome notification requested.';
}

export async function createRestaurantWithWelcome(payload: Record<string, unknown>) {
  const res = await api.post('/food/restaurants', payload);
  return {
    restaurant: res.data.data,
    welcomeNotify: res.data.meta?.welcomeNotify as WelcomeNotifyResult | undefined,
    welcomeEmailConfig: res.data.meta?.welcomeEmailConfig as WelcomeEmailConfigStatus | undefined,
  };
}
